const PIXELS_PER_LINE = 16;
const MAX_WHEEL_PIXELS = 160;

function wheelPixels(value, deltaMode, viewportHeight) {
  const unit = deltaMode === 1
    ? PIXELS_PER_LINE
    : deltaMode === 2
      ? Math.max(viewportHeight, 1)
      : 1;
  return Math.max(-MAX_WHEEL_PIXELS, Math.min(MAX_WHEEL_PIXELS, value * unit));
}

/** Translate browser wheel events into terrain-navigation gestures.
 * Trackpad two-finger scrolling and mouse wheels share the same glide path;
 * Chromium reports trackpad pinches as ctrl+wheel, which becomes camera zoom. */
export function terrainWheelGesture(event, viewportHeight = 800) {
  const x = wheelPixels(Number(event.deltaX) || 0, event.deltaMode, viewportHeight);
  const y = wheelPixels(Number(event.deltaY) || 0, event.deltaMode, viewportHeight);
  if (event.ctrlKey) return { type: "zoom", amount: y };
  return { type: "glide", right: x, forward: -y, boosted: Boolean(event.shiftKey) };
}

export function terrainZoomFov(currentFov, amount) {
  return Math.max(25, Math.min(75, currentFov + amount * 0.08));
}

/** Keyboard flight follows the selected camera mode. Pointer lock is an
 * optional mouse-look capability and must not gate Space/Ctrl/WASD. */
export function terrainFlightAcceptsKeys({ enabled, typing }) {
  return Boolean(enabled && !typing);
}

/** Resolve general viewer shortcuts after more specific tools have had the
 * opportunity to own the key event. */
export function viewerKeyboardShortcut(event, { toolOwnsKeyboard = false } = {}) {
  if (toolOwnsKeyboard) return null;
  if (event.defaultPrevented) return null;
  if (event.target?.matches?.("input, textarea, select, [contenteditable='true']")) return null;
  const key = String(event.key || "").toLowerCase();
  return ({ f: "fly", g: "grid", b: "bbox", t: "turntable" })[key] ?? null;
}

/** Character-scale free flight for large terrain previews. The general asset
 * viewer can move much faster, but inheriting that speed launches the terrain
 * camera tens of metres from the player in a fraction of a second. */
export function terrainFreeCameraSpeed(sizeM) {
  return Math.max(8, Math.min(24, (Number(sizeM) || 0) * 0.012));
}

/** Change camera-control ownership without allowing the control system to
 * reconcile the visible camera to its stale target. */
export function withPreservedCameraTransform(camera, transition) {
  const position = camera.position.clone();
  const quaternion = camera.quaternion.clone();
  try {
    return transition();
  } finally {
    camera.position.copy(position);
    camera.quaternion.copy(quaternion);
  }
}

/** Resolve the pointer gesture that owns first-person camera look.
 *
 * macOS Chromium may report Control+primary-click either as button 0 with
 * ctrlKey or as a synthetic secondary click (button 2). Treat both forms as
 * the same touchpad gesture. Option+primary-click is the other Mac-friendly
 * path; an unmodified primary click remains available for terrain editing.
 */
export function terrainLookGesture(event) {
  const button = Number(event.button);
  if (button === 0 && (event.altKey || event.ctrlKey)) {
    return { active: true, input: "touchpad" };
  }
  if (button === 2) {
    return { active: true, input: event.ctrlKey ? "touchpad" : "mouse" };
  }
  return { active: false, input: null };
}

/** Accept movement only from the pointer captured when camera look began.
 * Client coordinates are more reliable than movementX/Y for modifier drags
 * in macOS Chrome and extension-controlled browser sessions. */
export function terrainLookDelta(event, capturedPointerId, previousPoint) {
  if (capturedPointerId === null || capturedPointerId === undefined || event.pointerId !== capturedPointerId) return null;
  const clientX = Number(event.clientX);
  const clientY = Number(event.clientY);
  if (previousPoint && Number.isFinite(clientX) && Number.isFinite(clientY)) {
    return { movementX: clientX - previousPoint.x, movementY: clientY - previousPoint.y, clientX, clientY };
  }
  return {
    movementX: Number(event.movementX) || 0,
    movementY: Number(event.movementY) || 0,
    clientX: Number.isFinite(clientX) ? clientX : null,
    clientY: Number.isFinite(clientY) ? clientY : null,
  };
}

/** Integrate one grounded-camera gravity step in terrain metres. */
export function terrainFallStep({ currentY, targetY, velocity = 0, deltaSeconds, gravity = 32, terminalVelocity = 90 }) {
  const dt = Math.max(0, Math.min(Number(deltaSeconds) || 0, 0.1));
  if (currentY <= targetY || dt === 0) {
    return { y: currentY <= targetY ? targetY : currentY, velocity: currentY <= targetY ? 0 : velocity, landed: currentY <= targetY };
  }
  const nextVelocity = Math.max((Number(velocity) || 0) - gravity * dt, -Math.abs(terminalVelocity));
  const nextY = currentY + nextVelocity * dt;
  if (nextY <= targetY) return { y: targetY, velocity: 0, landed: true };
  return { y: nextY, velocity: nextVelocity, landed: false };
}
